/**
 * section-sw-pdp-info.js — PDP buy box logic.
 *
 * - Variant resolution across dropdown options + finish tiles (+ kit-type
 *   radios living in the product-kit-builder section, received via the
 *   `sw:kit-option` document event).
 * - Client-side price re-render into the price snippet's DOM (list price
 *   struck + our price accent — P-04 structure is server-rendered, JS only
 *   updates values/classes).
 * - Sticky ATC bar visibility (IntersectionObserver — deliberately NOT
 *   GSAP-gated so it works with animations disabled and under
 *   prefers-reduced-motion; P-05).
 * - Multi-item add-to-cart when addon checkboxes (addon-upsell section) are
 *   ticked: POST /cart/add.js with an items array, then go to the cart page.
 *   With no addons ticked the native form POST proceeds untouched (no-JS safe).
 * - Bundle addon finish auto-match (h6 §2D): keeps the bundle addon's variant
 *   in the same finish as the main product selection.
 * - V-brace stat-bar count-up (P-09) registered through the SWAnimations gate
 *   only; markup carries final values so no-JS/reduced-motion users see real
 *   numbers, never zeros.
 */
(function () {
  'use strict';
  if (window.SWPdpInfo) return;
  window.SWPdpInfo = true;

  function readJSON(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function formatter(currency) {
    try {
      var intl = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      });
      return function (cents) {
        return intl.format(cents / 100);
      };
    } catch (error) {
      return function (cents) {
        return (cents / 100).toFixed(2) + ' ' + (currency || '');
      };
    }
  }

  function init(root) {
    if (root.hasAttribute('data-sw-info-ready')) return;
    root.setAttribute('data-sw-info-ready', '');

    var variants = readJSON(root, '[data-sw-variants]') || [];
    var options = readJSON(root, '[data-sw-options]') || [];
    var finishNotes = readJSON(root, '[data-sw-finish-notes]') || {};
    var money = formatter(root.getAttribute('data-currency'));
    var form = root.querySelector('form[data-sw-product-form]');
    var variantInput = root.querySelector('[data-sw-variant-input]');
    var flyout = root.querySelector('[data-sw-finish-flyout]');
    var finishOptionName = root.getAttribute('data-finish-option') || 'Finish';

    /* ----- state: option values by position (1-based) ----- */
    var state = [];
    var currentId = parseInt(root.getAttribute('data-current-variant'), 10);
    var current = variants.filter(function (v) {
      return v.id === currentId;
    })[0] || variants[0];
    if (current) state = (current.options || []).slice();

    function variantMatching(values) {
      return variants.filter(function (v) {
        return (v.options || []).every(function (opt, i) {
          return values[i] === undefined || values[i] === null || opt === values[i];
        });
      })[0];
    }

    function resolve(changedPosition, changedValue) {
      var exact = variantMatching(state);
      if (exact) return exact;
      /* No exact combination — snap to the first variant carrying the changed
         value and adopt its full option set. */
      var fallback = variants.filter(function (v) {
        return (v.options || [])[changedPosition - 1] === changedValue;
      })[0];
      if (fallback) state = (fallback.options || []).slice();
      return fallback || current;
    }

    function syncControls() {
      root.querySelectorAll('[data-sw-option]').forEach(function (select) {
        var position = parseInt(select.getAttribute('data-option-position'), 10);
        if (state[position - 1] !== undefined && select.value !== state[position - 1]) {
          select.value = state[position - 1];
        }
      });
      root.querySelectorAll('[data-sw-finish]').forEach(function (tile) {
        var position = parseInt(tile.getAttribute('data-option-position'), 10);
        var selected = tile.getAttribute('data-value') === state[position - 1];
        tile.classList.toggle('is-selected', selected);
        tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    function renderPrice(variant) {
      var priceEl = root.querySelector('[data-sw-price] .price');
      if (!priceEl || !variant) return;
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--on-sale', !!onSale);
      priceEl.classList.toggle('price--sold-out', !variant.available);
      var regular = priceEl.querySelector('.price__regular .price-item--regular');
      if (regular) regular.textContent = money(variant.price);
      var saleCompare = priceEl.querySelector('.price__sale s.price-item--regular');
      if (saleCompare && onSale) saleCompare.textContent = money(variant.compare_at_price);
      var sale = priceEl.querySelector('.price-item--sale');
      if (sale) sale.textContent = money(variant.price);
    }

    function renderButtons(variant) {
      var available = !!(variant && variant.available);
      root.querySelectorAll('[data-primary-atc], [data-sw-bar-atc]').forEach(function (button) {
        button.disabled = !available;
      });
      var label = root.querySelector('[data-sw-atc-text]');
      if (label) {
        label.textContent = available
          ? label.getAttribute('data-label-add') || label.textContent
          : label.getAttribute('data-label-sold-out') || label.textContent;
      }
    }

    function finishValue() {
      var finishOption = options.filter(function (opt) {
        return opt.name === finishOptionName;
      })[0];
      return finishOption ? state[finishOption.position - 1] : null;
    }

    function syncBundleAddons(value) {
      if (!value) return;
      document.querySelectorAll('[data-sw-addon][data-sw-finish-map]').forEach(function (input) {
        var map;
        try {
          map = JSON.parse(input.getAttribute('data-sw-finish-map'));
        } catch (error) {
          return;
        }
        var entry = map && map[value];
        if (!entry) return;
        input.setAttribute('data-variant-id', entry.id);
        var keep = parseInt(input.getAttribute('data-bundle-keep'), 10);
        var effective = isNaN(keep) ? entry.price : Math.round((entry.price * keep) / 100);
        input.setAttribute('data-price', effective);
      });
    }

    function applyVariant(variant, meta) {
      if (!variant) return;
      current = variant;
      if (variantInput) variantInput.value = variant.id;
      renderPrice(variant);
      renderButtons(variant);
      var barPrice = root.querySelector('[data-atc-bar-price]');
      if (barPrice) barPrice.textContent = money(variant.price);
      var fin = finishValue();
      syncBundleAddons(fin);
      document.dispatchEvent(
        new CustomEvent('sw:variant-change', {
          detail: {
            variantId: variant.id,
            price: variant.price,
            compareAtPrice: variant.compare_at_price,
            available: variant.available,
            finishValue: fin,
            optionValues: state.slice(),
            galleryLayer: meta && meta.galleryLayer ? meta.galleryLayer : null
          }
        })
      );
    }

    /* ----- selects ----- */
    root.querySelectorAll('[data-sw-option]').forEach(function (select) {
      select.addEventListener('change', function () {
        var position = parseInt(select.getAttribute('data-option-position'), 10);
        state[position - 1] = select.value;
        var variant = resolve(position, select.value);
        syncControls();
        applyVariant(variant);
      });
    });

    /* ----- finish tiles ----- */
    root.querySelectorAll('[data-sw-finish]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        var position = parseInt(tile.getAttribute('data-option-position'), 10);
        var value = tile.getAttribute('data-value');
        state[position - 1] = value;
        var variant = resolve(position, value);
        syncControls();
        if (flyout) {
          var note = finishNotes[value];
          flyout.textContent = note ? 'Selected: ' + value + ' — ' + note : 'Selected: ' + value;
        }
        var layer = tile.getAttribute('data-target-layer');
        if (layer) {
          document.dispatchEvent(new CustomEvent('sw:gallery-show', { detail: { layer: layer } }));
        }
        applyVariant(variant, { galleryLayer: layer });
      });
    });

    /* ----- kit-type radios (product-kit-builder section) ----- */
    document.addEventListener('sw:kit-option', function (event) {
      var detail = event.detail || {};
      var option = options.filter(function (opt) {
        return opt.name === detail.name;
      })[0];
      if (!option) return;
      state[option.position - 1] = detail.value;
      var variant = resolve(option.position, detail.value);
      syncControls();
      applyVariant(variant);
    });

    /* ----- multi-item add (addons) ----- */
    if (form) {
      form.addEventListener('submit', function (event) {
        var checked = document.querySelectorAll('[data-sw-addon]:checked');
        if (!checked.length) return; // plain single-item add — let the native POST run
        event.preventDefault();
        var items = [{ id: parseInt(variantInput.value, 10), quantity: 1 }];
        checked.forEach(function (input) {
          var id = parseInt(input.getAttribute('data-variant-id'), 10);
          if (id) items.push({ id: id, quantity: 1 });
        });
        var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        fetch(addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          })
          .catch(function () {
            form.submit(); // fall back to the native single-item flow
          });
      });
    }

    /* ----- sticky ATC bar (P-05) ----- */
    var bar = root.querySelector('[data-atc-bar]');
    var primary = root.querySelector('[data-primary-atc]');
    if (bar && primary && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            bar.classList.toggle('is-visible', scrolledPast);
          });
        },
        { threshold: 0 }
      );
      observer.observe(primary);
    }

    /* announce initial state so sibling sections (kit builder) sync up */
    applyVariant(current);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-sw-pdp-info]').forEach(init);
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  }
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  /* ----- stat-bar count-up (V-brace/Ghost, P-09) — SWAnimations gate only.
     Markup ships final values; the tween runs 0 -> target when the bar enters
     the viewport. Under reduced motion register() is a no-op and the final
     values simply stay put. ----- */
  if (window.SWAnimations) {
    window.SWAnimations.register('sw-pdp-stats', '[data-sw-animate="pdp-stats"]', function (el, gsap) {
      var intensityHost = el.closest('[data-intensity]');
      var duration = intensityHost && intensityHost.getAttribute('data-intensity') === 'subtle' ? 0.6 : 1.2;
      var tweens = [];

      function formatValue(value, decimals) {
        if (decimals > 0) return value.toFixed(decimals);
        return Math.round(value).toLocaleString(document.documentElement.lang || 'en-GB');
      }

      function run() {
        el.querySelectorAll('[data-sw-count]').forEach(function (node) {
          var raw = String(node.getAttribute('data-target') || '');
          var target = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
          if (isNaN(target)) return;
          var decimals = (raw.split('.')[1] || '').length;
          var proxy = { value: 0 };
          node.textContent = formatValue(0, decimals);
          tweens.push(
            gsap.to(proxy, {
              value: target,
              duration: duration,
              ease: 'power1.out',
              onUpdate: function () {
                node.textContent = formatValue(proxy.value, decimals);
              }
            })
          );
        });
      }

      var trigger = null;
      if (window.ScrollTrigger) {
        trigger = window.ScrollTrigger.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: run
        });
      } else {
        run();
      }

      return function () {
        if (trigger) trigger.kill();
        tweens.forEach(function (tween) {
          tween.kill();
        });
      };
    });
  }
})();
